import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { Gems } from '@/components/Gems';

const TermsOfService = () => {
  return (
    <Layout>
      <div className="min-h-screen bg-background">
        <SEO
          title="Terms of Service"
          description="Read our Terms of Service to understand the rules and regulations for using IndieKeys."
        />

        <main className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl font-bold mb-8">Terms of Service</h1>

            <div className="prose prose-gray max-w-none">
              <p className="text-muted-foreground mb-8">
                Last updated: January 2026
              </p>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">1. Legal Notice & Identification</h2>
                <p>
                  The website <strong>IndieKeys</strong> (hereinafter "the Platform") is published and operated by:
                </p>
                <p className="mt-4">
                  <strong>PATATE DOUCE STUDIO</strong><br />
                  <strong>EGENSCHWILLER MIKE (EI)</strong><br />
                  Registered office: 4 rue de la ville, 67460 SOUFFELWEYERSHEIM, FRANCE<br />
                  SIRET Number: 909 268 419 00019<br />
                  Hosting Provider: IONOS SE (Germany) / Supabase (USA/EU) for data & auth services.
                </p>
                <p className="mt-4">
                  By creating an account or subscribing to our Services, you acknowledge that you are entering into a binding legal agreement with <strong>Mike Egenschwiller (EI)</strong>, governed by French law.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">2. Description of Service</h2>
                <p>
                  IndieKeys is a SaaS platform connecting independent game developers ("Developers"), content creators ("Creators"), and players ("Players").
                  The Service includes:
                </p>
                <ul className="list-disc pl-6 mt-4">
                  <li><strong>For Developers:</strong> Distribution of game keys, Press Kit hosting, visibility boosts.</li>
                  <li><strong>For Creators:</strong> Access to game keys for review and content creation purposes.</li>
                  <li><strong>For Players:</strong> Discovery of indie games, mission system to earn virtual rewards ("Coins").</li>
                </ul>
                <p className="mt-4">
                  <strong>IndieKeys acts solely as a technical intermediary.</strong> We do not develop the games listed (unless stated otherwise) and are not responsible for their content.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">3. Account Registration</h2>
                <p>
                  You must be at least 13 years old to use the Service.
                </p>
                <ul className="list-disc pl-6 mt-4">
                  <li><strong>Security:</strong> You are responsible for maintaining the confidentiality of your login credentials.</li>
                  <li><strong>Accuracy:</strong> You agree to provide accurate, current, and complete information.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">4. Paid Subscriptions & Payments</h2>

                <h3 className="text-xl font-semibold mb-3 mt-6">4.1. Subscription Terms</h3>
                <p>
                  Certain features (e.g., Premium, Boosts) are available as paid subscriptions.
                </p>
                <ul className="list-disc pl-6 mt-4">
                  <li><strong>Billing:</strong> Subscriptions are billed in advance on a recurring basis (monthly or annually).</li>
                  <li><strong>Automatic Renewal:</strong> Unless cancelled, your subscription will automatically renew at the end of each billing cycle.</li>
                </ul>

                <h3 className="text-xl font-semibold mb-3 mt-6">4.2. Cancellation Policy</h3>
                <p>
                  You may cancel your subscription at any time via your account settings.
                </p>
                <ul className="list-disc pl-6 mt-4">
                  <li><strong>Effect of Cancellation:</strong> Cancellation will stop the next automatic renewal charge.</li>
                  <li><strong>Service Continuity:</strong> Your access to Premium features will continue until the end of your current paid billing period.</li>
                  <li><strong>No Refunds:</strong> We do not provide refunds or credits for any partial subscription periods or unused services.</li>
                </ul>

                <h3 className="text-xl font-semibold mb-3 mt-6">4.3. Waiver of Right of Withdrawal</h3>
                <p>
                  <strong>IMPORTANT FOR EU CONSUMERS:</strong><br />
                  In accordance with Article L.221-28 of the French Consumer Code (<em>Code de la consommation</em>), by subscribing to a digital service (Premium, Boost) and accessing it immediately, you expressly acknowledge and agree to waive your 14-day right of withdrawal. Therefore, all sales are final and non-refundable.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">5. Player Shop & Digital Goods</h2>
                <p>
                  The Player Shop offers digital items such as <strong>Avatar Borders</strong> (cosmetic) and <strong>Gems Boosts</strong> (temporary modifiers).
                </p>
                <ul className="list-disc pl-6 mt-4">
                  <li><strong>Instant Delivery:</strong> Items are delivered immediately to your account inventory upon successful payment.</li>
                  <li><strong>No Resale:</strong> Digital items cannot be sold, traded, or transferred to other accounts.</li>
                  <li><strong>Usage:</strong> Cosmetic items remain available as long as your account is active. Boosts expire after the duration specified during purchase.</li>
                  <li><strong>Refunds:</strong> Since these are digital goods delivered instantly, they are non-refundable (see section 4.3).</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">6. Indie Studio Policy</h2>
                <p>
                  IndieKeys is exclusively reserved for <strong>independent game studios</strong>.
                  To qualify, studios must meet criteria such as independent development, no major publisher backing, and adherence to the indie spirit.
                </p>
                <p className="mt-4">
                  <strong>Moderation:</strong> We reserve the right to suspend any account that misrepresents its status (e.g., a hidden AAA studio) without refunding active subscriptions.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">7. Key Distribution & Anti-Fraud</h2>

                <h3 className="text-xl font-semibold mb-3 mt-6">7.1. For Creators</h3>
                <ul className="list-disc pl-6 mt-4">
                  <li>Keys are provided for promotional and testing purposes only.</li>
                  <li><strong>STRICT PROHIBITION:</strong> Selling, trading, or exchanging keys obtained via IndieKeys on third-party marketplaces is strictly forbidden.</li>
                  <li><strong>Consequences:</strong> Any user suspected of reselling keys will be permanently banned.</li>
                </ul>

                <h3 className="text-xl font-semibold mb-3 mt-6">7.2. For Developers</h3>
                <ul className="list-disc pl-6 mt-4">
                  <li>You retain full ownership of your keys.</li>
                  <li>You authorize IndieKeys to distribute them to Creators.</li>
                  <li>IndieKeys is not liable for keys misused by third parties, though we implement strict security measures.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">8. Virtual Currency ("<Gems with_text={true} />")</h2>
                <p>
                  The Platform includes a virtual point system called "<Gems with_text={true} />".
                </p>
                <ul className="list-disc pl-6 mt-4">
                  <li><strong>Earn Only:</strong> <Gems with_text={true} size={11} /> can only be earned by completing missions or activities on the Platform.</li>
                  <li><strong>No Purchase:</strong> <Gems with_text={true} size={11} /> cannot be purchased with real money.</li>
                  <li><strong>No Monetary Value:</strong> <Gems with_text={true} size={11} /> are not a cryptocurrency and have no real-world cash value. They cannot be exchanged for fiat currency.</li>
                  <li><strong>Usage:</strong> <Gems with_text={true} size={11} /> are solely used to unlock perks or obtain games within the IndieKeys ecosystem.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">9. User Content & Intellectual Property</h2>
                <p>
                  By uploading assets (images, trailers), you grant IndieKeys a license to display them solely for the purpose of promoting your game and the Platform.
                  The IndieKeys design, logo, and code are the exclusive property of <strong>Patate Douce Studio</strong>.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">10. Limitation of Liability</h2>
                <p>
                  The Service is provided "as is". IndieKeys shall not be liable for any indirect damages (data loss, game bugs, service interruption).
                  In no event shall IndieKeys' total liability exceed the amount you have paid to us in the last twelve (12) months.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">11. Governing Law & Jurisdiction</h2>
                <p>
                  These Terms are governed by the laws of <strong>France</strong>.
                  Any dispute arising out of or relating to these Terms shall be subject to the exclusive jurisdiction of the courts of <strong>Strasbourg, France</strong>.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">12. Contact</h2>
                <p>
                  For any legal inquiries, please contact us through our <a href="/contact" className="text-primary hover:underline">contact form</a>.
                </p>
              </section>

            </div>
          </div>
        </main>
      </div>
    </Layout>
  );
};

export default TermsOfService;